import React, { useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Images } from "../../../utils/Images";
import nominateVendorImage from "../../../assets/Images/Nominate_Vendor.png";
import { submitVendorNomination } from "../../../api/services/vendorNominationService";
import {
  sanitizeFormFieldValue,
  validateIndianMobile,
} from "../../../utils/phoneValidation";

const NominateVendorSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery("(min-width:1100px)");
  const isTablet = useMediaQuery("(min-width:600px) and (max-width:1099.95px)");
  const home = useSelector((state) => state.cms.home);

  const sections = Array.isArray(home?.data) ? home.data : [];
  const nominateCms = sections.find((item) => {
    if (!item) return false;
    const isActive = item.status === 1 || item.status === true;
    if (!isActive) return false;
    return String(item.section_title || "").toLowerCase().includes("feature a small vendor");
  });

  const mainHeading = nominateCms?.section_title || "Feature A Small Vendor & Change Their Life";
  const mainDescription =
    nominateCms?.title ||
    "Nominate a street vendor you know. We'll verify, feature them on our platform, list their products, and connect them with NGO partners for maximum impact.";
  const nominateImage = nominateCms?.image || nominateVendorImage;
  const overlayTitle = nominateCms?.sub_title || "Know a Hardworking Street Vendor?";
  const overlayDescription =
    nominateCms?.description ||
    "Nominate them to be featured on True Hope Foundation and help transform their livelihood.";

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: isMobile ? "3.988px" : "8px",
      minHeight: isMobile ? "40px" : "48px",
      "& fieldset": { borderColor: "#E8E8E8", borderWidth: isMobile ? "0.498px" : "1px" },
    },
    "& .MuiOutlinedInput-input": {
      padding: isMobile ? "10px 14px" : "12px 14px",
      fontSize: isMobile ? "12px" : "14px",
      color: "#1D1D1B",
    },
    "& .MuiOutlinedInput-input::placeholder": {
      color: "#979796",
      opacity: 1,
      fontSize: isMobile ? "12px" : "14px",
    },
    "& .MuiSelect-select": {
      padding: isMobile ? "10px 14px" : "12px 14px",
      fontSize: isMobile ? "12px" : "14px",
      color: "#979796",
    },
  };

  const initialForm = {
    vendor_name: "",
    vendor_mobile: "",
    your_name: "",
    phone_number: "",
    vendor_location: "",
    product_category: "",
    products_sold: "",
    vendor_story: "",
  };

  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChange =
    (key) =>
    (e) => {
      const value = sanitizeFormFieldValue(key, e?.target?.value ?? "");
      setForm((prev) => ({ ...prev, [key]: value }));
    };

  const validate = () => {
    const requiredKeys = [
      "vendor_name",
      "your_name",
      "vendor_location",
      "product_category",
      "products_sold",
    ];
    for (const k of requiredKeys) {
      if (!String(form[k] || "").trim()) return k;
    }
    const yourPhone = validateIndianMobile(form.phone_number);
    if (!yourPhone.isValid) return "phone_number";
    const vendorPhone = validateIndianMobile(form.vendor_mobile);
    if (!vendorPhone.isValid) return "vendor_mobile";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const missingKey = validate();
    if (missingKey) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please fill all required fields before submitting.",
      });
      return;
    }

    setSubmitting(true);
    try {
      await submitVendorNomination({
        vendor_name: form.vendor_name,
        vendor_mobile: validateIndianMobile(form.vendor_mobile).digits,
        your_name: form.your_name,
        phone_number: validateIndianMobile(form.phone_number).digits,
        vendor_location: form.vendor_location,
        product_category: form.product_category,
        products_sold: form.products_sold,
        vendor_story: form.vendor_story || "Optional story",
        status: 1,
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Nomination submitted successfully.",
      });
      setForm(initialForm);
    } catch (err) {
      const msg = err?.message || "Failed to submit nomination. Please try again later.";
      setSnackbar({
        open: true,
        severity: "error",
        message: msg,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "94.44%",
        maxWidth: "1440px",
        mx: "auto",
        mt: isMobile ? "20px" : "40px",
        mb: isMobile ? "30px" : "60px",
        px: isMobile ? "16px" : 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isDesktop ? "row" : "column",
          justifyContent: "flex-start",
          alignItems: "stretch",
          gap: isMobile ? "20px" : isTablet ? "28px" : "24px",
        }}
      >
        <Box
          sx={{
            width: isDesktop ? "auto" : "100%",
            flex: isDesktop ? 1 : "none",
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "13px" : "12px", mb: isMobile ? "14px" : "16px" }}>
            <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: "#8EA946" }} />
            <Typography sx={{ fontSize: isMobile ? "10px" : "14px", letterSpacing: "2px", color: "#131313", textTransform: "uppercase", lineHeight: 1 }}>
              Nominate a Vendor Today
            </Typography>
          </Box>

          <Typography
            sx={{
              fontWeight: 800,
              color: "#1D1D1B",
              fontSize: isMobile ? "18px" : "36px",
              lineHeight: isMobile ? "29.124px" : "58px",
              letterSpacing: isMobile ? "-0.36px" : "normal",
              mb: "10px",
              maxWidth: isMobile ? "none" : isDesktop ? "500px" : "100%",
            }}
          >
            {mainHeading}
          </Typography>

          <Typography
            sx={{
              fontWeight: 400,
              color: isMobile ? "#7E7878" : "#4A4A49",
              fontSize: isMobile ? "12px" : "16px",
              lineHeight: isMobile ? "19.416px" : "28px",
              letterSpacing: isMobile ? "-0.24px" : "-0.16px",
              mb: isDesktop ? "24px" : "16px",
              maxWidth: isMobile ? "none" : isDesktop ? "674px" : "100%",
            }}
          >
            {mainDescription}
          </Typography>

          {isDesktop && (
            <Box
              sx={{
                width: "100%",
                borderRadius: "20px",
                overflow: "hidden",
                position: "relative",
                height: isMobile ? "360px" : isTablet ? "440px" : "555px",
              }}
            >
              <Box component="img" src={nominateImage} alt="Street vendor market" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />

              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  px: "20px",
                  pt: "20px",
                  pb: "24px",
                  borderRadius: "0 0 20px 20px",
                  background: "rgba(0,0,0,0.5)",
                  backdropFilter: "blur(25px)",
                  WebkitBackdropFilter: "blur(25px)",
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "20px" : "32px", lineHeight: isMobile ? "28px" : "32px", color: "#fff", mb: "12px" }}>
                  {overlayTitle}
                </Typography>
                <Typography sx={{ fontWeight: 400, fontSize: "14px", lineHeight: "23px", color: "#E8E8E8", maxWidth: "514px" }}>
                  {overlayDescription}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            width: isDesktop ? "auto" : "100%",
            flex: isDesktop ? 1 : "none",
            minWidth: 0,
            border: "1px solid #F1F1F1",
            borderRadius: isMobile ? "9.97px" : "20px",
            background: "#FFFFFF",
            boxShadow: isMobile ? "0px 1.495px 3.489px rgba(0,0,0,0.09)" : "0px 3px 7px rgba(0,0,0,0.09)",
            px: isMobile ? "15px" : "24px",
            py: isMobile ? "20px" : "40px",
            flexShrink: 1,
          }}
        component="form"
        onSubmit={handleSubmit}
        >
          <Typography sx={{ display: "flex", alignItems: "center", gap: isMobile ? "4.985px" : "10px", fontWeight: 700, fontSize: isMobile ? "14px" : "32px", lineHeight: isMobile ? "14px" : "20px", letterSpacing: isMobile ? "-0.28px" : "normal", color: "#1D1D1B", mb: isMobile ? "14px" : "20px" }}>
            <Box component="img" src={Images.streamlineSharpStar} alt="star" sx={{ width: isMobile ? "14px" : "20px", height: isMobile ? "14px" : "20px" }} />
            Empower a Street Vendor
          </Typography>
          <Box sx={{ borderBottom: "1px solid #E8E8E8", mb: isMobile ? "16px" : "20px" }} />

          <Box sx={{ display: "flex", gap: isMobile ? "16px" : "21px", flexDirection: { xs: "column", sm: "row" }, mb: isMobile ? "16px" : "21px" }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "12px" : "16px", color: "#141414", mb: isMobile ? "8px" : "12px" }}>Vendor&apos;s Name</Typography>
              <TextField fullWidth placeholder="Enter Vendor's Name" sx={fieldSx} value={form.vendor_name} onChange={handleChange("vendor_name")} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "12px" : "16px", color: "#141414", mb: isMobile ? "8px" : "12px" }}>Your Name</Typography>
              <TextField fullWidth placeholder="Enter Your Name" sx={fieldSx} value={form.your_name} onChange={handleChange("your_name")} />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: isMobile ? "16px" : "21px", flexDirection: { xs: "column", sm: "row" }, mb: isMobile ? "16px" : "21px" }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "12px" : "16px", color: "#141414", mb: isMobile ? "8px" : "12px" }}>Phone Number</Typography>
              <TextField fullWidth placeholder="Enter Phone Number" sx={fieldSx} value={form.phone_number} onChange={handleChange("phone_number")} type="tel" inputProps={{ inputMode: "numeric", maxLength: 10 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "12px" : "16px", color: "#141414", mb: isMobile ? "8px" : "12px" }}>Vendor&apos;s Location</Typography>
              <TextField fullWidth placeholder="Enter Location" sx={fieldSx} value={form.vendor_location} onChange={handleChange("vendor_location")} />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: isMobile ? "16px" : "21px", flexDirection: { xs: "column", sm: "row" }, mb: isMobile ? "16px" : "21px" }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "12px" : "16px", color: "#141414", mb: isMobile ? "8px" : "12px" }}>Vendor&apos;s Phone Number</Typography>
              <TextField fullWidth placeholder="Enter Vendor's Phone number" sx={fieldSx} value={form.vendor_mobile} onChange={handleChange("vendor_mobile")} type="tel" inputProps={{ inputMode: "numeric", maxLength: 10 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "12px" : "16px", color: "#141414", mb: isMobile ? "8px" : "12px" }}>Product Category</Typography>
              <TextField
                select
                fullWidth
                value={form.product_category}
                onChange={handleChange("product_category")}
                SelectProps={{ displayEmpty: true, IconComponent: () => <Box component="img" src={Images.downArrow} alt="dropdown" sx={{ width: isMobile ? "18px" : "20px", height: isMobile ? "18px" : "20px", mr: "12px" }} /> }}
                sx={fieldSx}
              >
                <MenuItem value="" disabled>Select Category</MenuItem>
                <MenuItem value="Street food">Street food</MenuItem>
                <MenuItem value="Fresh produce">Fresh produce</MenuItem>
                <MenuItem value="Handicrafts">Handicrafts</MenuItem>
              </TextField>
            </Box>
          </Box>

          <Box sx={{ mb: isMobile ? "16px" : "21px" }}>
            <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "12px" : "16px", color: "#141414", mb: isMobile ? "8px" : "12px" }}>What products do they sell?</Typography>
            <TextField fullWidth placeholder="List Their Main Products" sx={fieldSx} value={form.products_sold} onChange={handleChange("products_sold")} />
          </Box>

          <Box sx={{ mb: isMobile ? "18px" : "24px" }}>
            <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "12px" : "16px", color: "#141414", mb: isMobile ? "8px" : "12px" }}>Product Category</Typography>
            <TextField
              fullWidth
              multiline
              minRows={isMobile ? 3 : 4}
              placeholder="Tell us about this vendor"
              sx={{
                ...fieldSx,
                "& .MuiOutlinedInput-root": {
                  ...fieldSx["& .MuiOutlinedInput-root"],
                  minHeight: isMobile ? "86px" : "auto",
                },
                "& .MuiOutlinedInput-inputMultiline": {
                  padding: isMobile ? "14px" : "12px 14px",
                },
              }}
              value={form.vendor_story}
              onChange={handleChange("vendor_story")}
            />
          </Box>

          <Button
            variant="contained"
            type="submit"
            disabled={submitting}
            sx={{
              width: isMobile ? "100%" : "auto",
              height: isMobile ? "40px" : "auto",
              px: isMobile ? "17.266px" : "30px",
              py: isMobile ? "10.791px" : "15px",
              borderRadius: isMobile ? "7.194px" : "10px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: isMobile ? "12px" : "14px",
              lineHeight: isMobile ? "14px" : "14px",
              background: "#F04951",
              boxShadow: isMobile ? "0px 2.878px 0px #141414" : "0px 4px 0px #141414",
              "&:hover": { background: "#F04951" },
            }}
          >
            {submitting ? "Submitting..." : "Submit Nominations"}
          </Button>

          <Dialog
            open={snackbar.open}
            onClose={handleSnackbarClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "16px",
                boxShadow: "0px 24px 80px rgba(0,0,0,0.15)",
                px: "24px",
                py: "18px",
                background: "#FFFFFF",
              },
            }}
          >
            <DialogTitle
              sx={{
                p: 0,
                mb: "10px",
                textAlign: "center",
                fontWeight: 800,
                fontSize: "18px",
                color: "#1D1D1B",
              }}
            >
              {snackbar.severity === "success" ? "Nomination Submitted" : "Submission Failed"}
            </DialogTitle>

            <DialogContent sx={{ p: 0, mb: "12px" }}>
              <Box
                sx={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  mx: "auto",
                  mb: "12px",
                  display: "grid",
                  placeItems: "center",
                  background: snackbar.severity === "success" ? "#F5F8ED" : "rgba(240, 73, 81, 0.10)",
                  border: snackbar.severity === "success" ? "1px solid rgba(209, 223, 173, 1)" : "1px solid rgba(240, 73, 81, 0.35)",
                }}
              >
                <Box
                  component="img"
                  src={snackbar.severity === "success" ? Images.greenBgTick : Images.failureIcon}
                  alt="status"
                  sx={{ width: "22px", height: "22px" }}
                />
              </Box>

              <Typography
                sx={{
                  textAlign: "center",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "22px",
                  color: "#4A4A49",
                }}
              >
                {snackbar.message}
              </Typography>
            </DialogContent>

            <DialogActions sx={{ p: 0, justifyContent: "center" }}>
              <Button
                onClick={handleSnackbarClose}
                variant="contained"
                sx={{
                  width: "160px",
                  height: "44px",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 800,
                  background: "#F04951",
                  boxShadow: "0px 4px 0px #141414",
                  "&:hover": { background: "#F04951" },
                }}
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
};

export default NominateVendorSection;
